import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type UsePageFiltersStorageOptions = {
  enabled?: boolean;
  excludeKeys?: string[];
  storageKeyPrefix?: string;
};

const DEFAULT_EXCLUDE_KEYS = ['page', 'perPage'];
const DEFAULT_STORAGE_KEY_PREFIX = 'alea_page_filters';

const normalizeSearch = (search: string) => {
  if (!search) return '';
  return search.startsWith('?') ? search.slice(1) : search;
};

type StoredPageFiltersMap = Record<string, string>;

const getMapPathKey = (pathname: string) => pathname;

const readStoredPageFiltersMap = (storageKey: string): StoredPageFiltersMap => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as StoredPageFiltersMap;
  } catch {
    return {};
  }
};

const writeStoredPageFiltersMap = (
  storageKey: string,
  map: StoredPageFiltersMap
) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    return;
  }
};

const cleanSearchString = (search: string, excludeKeys: string[]) => {
  const normalized = normalizeSearch(search);
  if (!normalized) return null;

  const params = new URLSearchParams(normalized);
  excludeKeys.forEach((key) => params.delete(key));

  const cleaned = params.toString();
  if (!cleaned) return null;
  return cleaned;
};

const areSearchStringsEquivalent = (a: string, b: string) => {
  const aParams = new URLSearchParams(normalizeSearch(a));
  const bParams = new URLSearchParams(normalizeSearch(b));

  const entriesToKey = (params: URLSearchParams) => {
    const map = new Map<string, string[]>();
    params.forEach((value, key) => {
      const current = map.get(key);
      if (current) {
        current.push(value);
      } else {
        map.set(key, [value]);
      }
    });
    for (const [key, values] of map.entries()) {
      values.sort();
      map.set(key, values);
    }
    return map;
  };

  const aMap = entriesToKey(aParams);
  const bMap = entriesToKey(bParams);
  if (aMap.size !== bMap.size) return false;

  for (const [key, aValues] of aMap.entries()) {
    const bValues = bMap.get(key);
    if (!bValues) return false;
    if (aValues.length !== bValues.length) return false;
    for (let i = 0; i < aValues.length; i += 1) {
      if (aValues[i] !== bValues[i]) return false;
    }
  }
  return true;
};

const buildMergedSearchWithExcludedKeys = (
  currentSearch: string,
  storedSearch: string,
  excludeKeys: string[]
) => {
  const currentNormalized = normalizeSearch(currentSearch);
  const storedNormalized = normalizeSearch(storedSearch);

  const currentParams = new URLSearchParams(currentNormalized);
  const storedParams = new URLSearchParams(storedNormalized);

  const merged = new URLSearchParams(storedParams.toString());
  excludeKeys.forEach((key) => {
    const value = currentParams.get(key);
    if (value !== null) merged.set(key, value);
  });

  const mergedString = merged.toString();
  return mergedString ? `?${mergedString}` : '';
};

const getStoredSearchForRestore = (
  storageKey: string,
  pathname: string,
  excludeKeys: string[]
) => {
  try {
    const mapPathKey = getMapPathKey(pathname);

    const map = readStoredPageFiltersMap(storageKey);
    const storedFromMap = map[mapPathKey];
    if (storedFromMap) {
      const cleaned = cleanSearchString(storedFromMap, excludeKeys);
      return cleaned ? `?${cleaned}` : null;
    }

    return null;
  } catch {
    return null;
  }
};

export const usePageFiltersStorage = (
  options: UsePageFiltersStorageOptions = {}
) => {
  const {
    enabled = true,
    excludeKeys = DEFAULT_EXCLUDE_KEYS,
    storageKeyPrefix = DEFAULT_STORAGE_KEY_PREFIX,
  } = options;

  const location = useLocation();
  const navigate = useNavigate();

  const mapPathKey = useMemo(() => getMapPathKey(location.pathname), [location.pathname]);

  const [isRestoring, setIsRestoring] = useState(() => {
    if (!enabled) return false;

    const storedForRestore = getStoredSearchForRestore(
      storageKeyPrefix,
      location.pathname,
      excludeKeys
    );
    if (!storedForRestore) return false;

    const currentHasAnyFilterKey =
      cleanSearchString(location.search, excludeKeys) !== null;
    if (currentHasAnyFilterKey) return false;

    return true;
  });
  const restoreTargetSearchRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const storedForRestore = getStoredSearchForRestore(
      storageKeyPrefix,
      location.pathname,
      excludeKeys
    );
    if (!storedForRestore) return;

    const currentHasAnyFilterKey =
      cleanSearchString(location.search, excludeKeys) !== null;
    if (currentHasAnyFilterKey) return;

    const merged = buildMergedSearchWithExcludedKeys(
      location.search,
      storedForRestore,
      excludeKeys
    );

    restoreTargetSearchRef.current = merged;
    setIsRestoring(true);
    navigate(`${location.pathname}${merged}`, { replace: true });
  }, [
    enabled,
    excludeKeys,
    location.pathname,
    location.search,
    navigate,
    storageKeyPrefix,
  ]);

  useEffect(() => {
    const target = restoreTargetSearchRef.current;
    if (!target) return;
    if (areSearchStringsEquivalent(location.search, target)) {
      restoreTargetSearchRef.current = null;
      setIsRestoring(false);
    }
  }, [location.search]);

  useEffect(() => {
    if (!enabled) return;

    const target = restoreTargetSearchRef.current;
    if (target && !areSearchStringsEquivalent(location.search, target)) return;

    const normalized = normalizeSearch(location.search);
    const params = new URLSearchParams(normalized);
    excludeKeys.forEach((key) => params.delete(key));

    try {
      const map = readStoredPageFiltersMap(storageKeyPrefix);
      const nextValue = params.toString();

      const hasOnlyExcludedKeys = normalized !== '' && nextValue === '';
      if (hasOnlyExcludedKeys) return;

      if (nextValue) {
        map[mapPathKey] = nextValue;
      } else {
        delete map[mapPathKey];
      }
      writeStoredPageFiltersMap(storageKeyPrefix, map);
    } catch {
      return;
    }
  }, [enabled, excludeKeys, location.search, mapPathKey, storageKeyPrefix]);

  return { isRestoring };
};
